import { DriverEntity } from "./driverEntity";
import { TruckEntity } from "./truckEntity";

/** ピッキングリストの出力順。 */
export type PickingSortMode = 'course-shipper' | 'shipper-course';

/** 既定の出力順（既存挙動 = コース優先）。 */
export const DEFAULT_PICKING_SORT_MODE: PickingSortMode = 'course-shipper';

export type PickingList = {
  truck: TruckEntity;
  driver: DriverEntity;
  course_number?: number | null;
  dispatches: DateList[];
}

export type DateList = {
  date: string;
  shippers: ShipperList[];
}

export type ShipperList = {
  shipper_name: string,
  unloads: UnloadList[]
}

export type UnloadList = {
  unloading_name: string,
  items: ItemList[]
  slip_numbers?: string;
  slip_number_list?: string[];
}

export type ItemList = {
  item_carrier_company: string;
  item_code: string;
  item_count: number;
  item_name: string,
  item_lot_number?: string | null;
  item_packing_style: string;
  item_memo?: string | null;
  loading_time_range_start: string;
  unloading_address: string;
  unloading_time_range_start: string;
  empty_id?: number;
}

/** バーコードを 1 段に何個並べるか (CSS grid と合わせる)。 */
export const BARCODE_PER_ROW = 3;

/** バーコード非対象または 0〜1 段時の 1 ページあたり明細行数。 */
export const PICKING_PAGE_ROWS_DEFAULT = 30;

/** バーコード段数に達した時の 1 ページあたり明細行数の下限。 */
export const PICKING_PAGE_ROWS_MIN = 10;

/** バーコード 1 段あたりの明細減算行数 (2 段目以降で適用)。 */
export const PICKING_ROWS_PER_BARCODE_ROW = 3;

/**
 * バーコード段数に応じた 1 ページあたり明細行数を返す。
 * 0〜1 段はデフォルト、2 段目以降はバーコード 1 段増えるごとに明細を 3 行ずつ減算、
 * PICKING_PAGE_ROWS_MIN で打ち止め。
 *  - 非対象 or 0 個: 30
 *  - 1 段 (1〜3 個): 30
 *  - 2 段 (4〜6 個): 27
 *  - 3 段 (7〜9 個): 24
 *  - 4 段 (10〜12 個): 21
 *  - 5 段 (13〜15 個): 18
 *  - 6 段 (16〜18 個): 15
 *  - 7 段 (19〜21 個): 12
 *  - 8 段以上 (22 個以上): 10 (下限打ち止め)
 */
export function pickingPageRowsForBarcodes(slipCount: number, isBarcode: boolean): number {
  if (!isBarcode || slipCount === 0) return PICKING_PAGE_ROWS_DEFAULT;
  const rows = Math.ceil(slipCount / BARCODE_PER_ROW);
  return Math.max(
    PICKING_PAGE_ROWS_MIN,
    PICKING_PAGE_ROWS_DEFAULT - Math.max(0, rows - 1) * PICKING_ROWS_PER_BARCODE_ROW,
  );
}

/** 1 納品先の商品行数から、rowsPerPage ごとに分割したページ数を返す (最低 1 ページ)。 */
export function pickingUnloadPageCount(itemCount: number, rowsPerPage: number): number {
  return Math.max(1, Math.ceil(itemCount / rowsPerPage));
}

/**
 * ピッキングリスト全体の総ページ数を、unload ごとのバーコード段数で動的に計算する。
 * Component (onCountChange) と Presenter (フッターの x/y 表示) で共有する。
 */
export function computePickingPageCount(
  models: PickingList[],
  barcodeShipperSet: Set<string>,
): number {
  return models.reduce((total, course) => {
    if (course.dispatches.length === 0) return total + 1;
    const courseCount = course.dispatches.reduce((dispatchTotal, loadingDate) => {
      const dateCount = loadingDate.shippers.reduce(
        (shipperTotal, shipper) => shipperTotal
          + shipper.unloads.reduce((unloadTotal, unload) => {
            const isBarcode = barcodeShipperSet.has(shipper.shipper_name);
            const slipCount = getSlipNumbers(unload).length;
            const rowsPerPage = pickingPageRowsForBarcodes(slipCount, isBarcode);
            return unloadTotal + pickingUnloadPageCount(unload.items?.length ?? 0, rowsPerPage);
          }, 0),
        0,
      );
      return dispatchTotal + dateCount;
    }, 0);
    return total + courseCount;
  }, 0);
}

/**
 * 伝票 No 配列を取得する。
 *  - slip_number_list が非空なら優先
 *  - 空 / undefined なら slip_numbers (CSV) を trim・空要素除外して返す
 *
 * Presenter (SlipNumberValue) とページ数計算 (computePickingPageCount) で共有することで、
 * バーコード段数判定と実描画のズレを防ぐ。
 */
export function getSlipNumbers(unload: UnloadList): string[] {
  return unload.slip_number_list?.length
    ? unload.slip_number_list
    : (unload.slip_numbers ?? '').split(',').map((s) => s.trim()).filter(Boolean);
}
